import { ChangeDetectionStrategy, Component, inject, signal } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router, RouterModule } from "@angular/router";
import { ButtonComponent, ButtonType, InputOutline } from "@sxm-content-creators/tools-ds";

import { SnackbarService } from "../../components/snackbar/snackbar.service";
import { AuthService } from "../auth.service";
import { passwordRequirementsList, passwordsMatchValidator, passwordStrengthValidator } from "../password-validators";

@Component({
    selector: "app-forgot-password",
    templateUrl: "./forgot-password.component.html",
    styleUrl: "./forgot-password.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [ReactiveFormsModule, RouterModule, ButtonComponent, InputOutline]
})
export class ForgotPasswordComponent {
    private readonly router = inject(Router);
    private readonly snackbarService = inject(SnackbarService);
    private readonly authService = inject(AuthService);

    readonly ButtonType = ButtonType;
    readonly isLoading = signal(false);
    readonly codeSent = signal(false);
    readonly passwordReset = signal(false);
    readonly storedEmail = signal("");
    readonly resendSuccess = signal(false);

    private resendTimer: ReturnType<typeof setInterval> | null = null;

    readonly passwordRequirementsList = passwordRequirementsList;

    requestForm = new FormGroup({
        email: new FormControl("", [Validators.required, Validators.email])
    });

    confirmForm = new FormGroup(
        {
            code: new FormControl("", [Validators.required]),
            newPassword: new FormControl("", [Validators.required, passwordStrengthValidator()]),
            confirmPassword: new FormControl("", [Validators.required])
        },
        {
            validators: passwordsMatchValidator()
        }
    );

    get isRequestSubmitDisabled(): boolean {
        return this.requestForm.invalid || this.isLoading();
    }

    get isConfirmSubmitDisabled(): boolean {
        return this.confirmForm.invalid || this.isLoading();
    }

    get passwordStarted(): boolean {
        return (this.confirmForm.get("newPassword")?.value ?? "").length > 0;
    }

    get passwordRequirements(): { label: string; met: boolean }[] {
        const value: string = this.confirmForm.get("newPassword")?.value ?? "";
        return this.passwordRequirementsList.map(req => ({ label: req.label, met: req.check(value) }));
    }

    onRequestReset(): void {
        if (this.requestForm.invalid) {
            return;
        }

        const { email } = this.requestForm.value;

        this.isLoading.set(true);

        this.authService.forgotPassword(email!).subscribe({
            next: () => {
                this.isLoading.set(false);
                this.storedEmail.set(email!);
                this.codeSent.set(true);
            },
            error: () => {
                this.isLoading.set(false);
                this.snackbarService.show("Could not send reset code. Please try again.", "error");
            }
        });
    }

    onConfirmReset(): void {
        if (this.confirmForm.invalid) {
            return;
        }

        const { code, newPassword } = this.confirmForm.value;

        this.isLoading.set(true);

        this.authService.confirmForgotPassword(this.storedEmail(), code!, newPassword!).subscribe({
            next: () => {
                this.isLoading.set(false);
                this.passwordReset.set(true);
            },
            error: (err: { message?: string }) => {
                this.isLoading.set(false);
                const message = err?.message || "Invalid or expired code. Please check your email and try again.";
                this.snackbarService.show(message, "error");
            }
        });
    }

    signInWithOkta(): void {
        this.authService.signInWithOkta();
    }

    onResendCode(event: Event): void {
        event.preventDefault();

        this.authService.forgotPassword(this.storedEmail()).subscribe();

        this.resendSuccess.set(true);

        if (this.resendTimer) {
            clearInterval(this.resendTimer);
        }

        this.resendTimer = setInterval(() => {
            clearInterval(this.resendTimer!);
            this.resendTimer = null;
            this.resendSuccess.set(false);
        }, 30000);
    }

    goToLogin(): void {
        this.router.navigate(["/login"]);
    }
}
